// #region imports
import { call, delay, put, select } from 'redux-saga/effects';
import {
  cellHover,
  mouseMoveWithSelection,
  startCellSelection,
  startSelectionInteraction,
  stopCellSelection,
} from './viewsActions';
import { updateSheetView } from '../../sheetsManagerSlice';
import {
  Area,
  CellPosition,
  SelectionCell,
  SelectionOperationType,
  Sheet,
  SheetSelection,
  SheetView,
  TemporarySelection,
} from '../../../types';
import {
  expandSelectionArea,
  setActiveCell,
  getCellPositionFromClientPosition,
} from '../../../utils/selection/selection';
import { RootState } from '../../../../core/redux/store';
import {
  SheetAndView,
  selectSheet,
  selectSheetAndView,
  selectSheetView,
} from '../../../selectors/sheetSelector';
import _ from 'lodash';
import { findWindowBySheetId } from '../../../../windows/redux/windowsSlice';
import { AppWindow } from '../../../../windows/types';
import { getPanesBoundaries } from '../../../utils/views/views';
import {
  Boundaries,
  getCellPositionFromMousePosition,
} from '../../../utils/views/moveSelection';
import { inRange } from '../../../../core/utils/general/general';
import { handleMouseHoverSelection } from '../../../utils/views/selection';
import { getI18n } from 'react-i18next';
import {
  convertPositionToArea,
  convertPositionToSheetSelection,
  createSheetSelection,
  isCellInsideArea,
} from '../../../utils/area/area';
import { updateSelectionOnMouseStop } from '../../../utils/views/stopSelection';
import { unionAreaWithMerges } from '../../../utils/views/merges';
import { handleSheetOperations } from '../root/rootActions';
import { SheetCommandType } from '../../../types/commands/sheetCommands/sheetCommands';
// #endregion

let mouseDown = false;

// #region cell selection / mouse down on cell
export function* cellSelectionMi({
  payload,
}: ReturnType<typeof startCellSelection>) {
  const { sheetId, cell, position, ctrlKey, shiftKey, forContextMenu } =
    payload;
  const sheet: Sheet = yield select((state: RootState) =>
    selectSheet(state, sheetId)
  );
  const sheetView: SheetView = yield select((state: RootState) =>
    selectSheetView(state, sheetId)
  );

  // #region Testing cell data save
  // TODO: Remove code after implementing cell inpu
  if (cell === SelectionCell.Cell) {
    let text = '';
    const el = document.getElementById('temp-cell-editor');
    if (el) text = (el as HTMLInputElement).value;

    if (text.length) {
      yield put(
        handleSheetOperations({
          type: SheetCommandType.CellInput,
          position,
          value: text,
          sheetId: sheet.id,
        })
      );
      // yield put(
      //   handleCellOperationsAc({
      //     sheetId,
      //     operationData: {
      //       op: CellOperations.Input,
      //       position,
      //       input: text, //'=Sheet1!A1+10',
      //     },
      //   })
      // );
    }
  }
  // #endregion testing

  let selections = _.cloneDeep(sheetView.selections);

  let activeCellSet = {
    activeCell: sheetView.activeCell,
    activeCellArea: sheetView.activeCellArea,
  };

  if (forContextMenu) {
    const activeSel = _.last(selections);

    if (activeSel)
      if (
        isCellInsideArea(position, activeSel.area) &&
        (activeSel.cell === cell || cell === SelectionCell.Cell)
      ) {
        return;
      }
  }

  // let activeCell = { ...sheetView.activeCell };
  // let activeCellArea = _.cloneDeep(sheetView.activeCellArea);

  if (shiftKey || cell === SelectionCell.Root) {
    const area = expandSelectionArea(
      activeCellSet.activeCell,
      position,
      // sheet.maxRowIndex,
      // sheet.maxColIndex,
      cell,
      sheet
    );
    selections = [{ area, cell }];
    if (!shiftKey)
      activeCellSet = setActiveCell(
        position,
        sheetView.colViewport,
        sheetView.rowViewport,
        cell,
        sheet
      );
  } else {
    activeCellSet = setActiveCell(
      position,
      sheetView.colViewport,
      sheetView.rowViewport,
      cell,
      sheet
    );

    // console.log('activeCellSet', activeCellSet);

    const area = expandSelectionArea(
      activeCellSet.activeCell,
      activeCellSet.activeCell,
      // sheet.maxRowIndex,
      // sheet.maxColIndex,
      cell,
      sheet
    );

    // console.log('area', area);

    const selection: SheetSelection = { area, cell };

    if (ctrlKey) _.set(selections, selections.length, selection);
    else selections = [selection];
  }

  if (cell !== SelectionCell.Root) mouseDown = true;

  yield put(
    updateSheetView({
      sheetId,
      selecting: cell !== SelectionCell.Root,
      selections,
      activeCell: activeCellSet.activeCell,
      activeCellArea: activeCellSet.activeCellArea,
    })
  );
}
// #endregion

// #region selection interaction
export function* startSelectionInteractionMi({
  payload,
}: ReturnType<typeof startSelectionInteraction>) {
  const {
    sheetId,
    clientX,
    clientY,
    op,
    freezedColsSum,
    freezedRowsSum,
    positionIndex,
    // forContextMenu,
  } = payload;
  const { sheet, sheetView }: SheetAndView = yield select((state) =>
    selectSheetAndView(state, sheetId)
  );
  const window: AppWindow | undefined = yield select(
    findWindowBySheetId(sheetId)
  );
  if (!window) return;
  const isRtl = getI18n().dir() === 'rtl';

  let temporarySelection: TemporarySelection | undefined;
  let selections: SheetSelection[] | undefined;
  let activeCell: CellPosition | undefined;
  let activeCellArea: Area | undefined;

  const activeSelection =
    _.last(sheetView.selections) ??
    convertPositionToSheetSelection(sheetView.activeCell, SelectionCell.Cell);

  // if (forContextMenu) {
  //   const pos = getCellPositionFromClientPosition(
  //     window,
  //     sheet,
  //     sheetView,
  //     freezedColsSum,
  //     freezedRowsSum,
  //     clientX,
  //     clientY,
  //     isRtl,
  //     activeSelection
  //   );
  //   console.log('pos', pos);
  // } else {
  switch (op) {
    case SelectionOperationType.Extend:
      temporarySelection = {
        area: activeSelection.area,
        op,
      };
      break;
    case SelectionOperationType.ColMove:
    case SelectionOperationType.RowMove:
      if (positionIndex !== undefined) {
        temporarySelection = {
          op,
          initialPositionIndex: positionIndex,
          area: activeSelection.area,
        };
      }
      break;
    case SelectionOperationType.SelectionMove:
      const pos = getCellPositionFromClientPosition(
        window,
        sheet,
        sheetView,
        freezedColsSum,
        freezedRowsSum,
        clientX,
        clientY,
        isRtl,
        activeSelection
      );

      const { start, end } = activeSelection.area;
      pos.ci = Math.max(start.ci, Math.min(pos.ci, end.ci));
      pos.ri = Math.max(start.ri, Math.min(pos.ri, end.ri));

      // console.log('pos', pos);
      temporarySelection = {
        op,
        area: activeSelection.area,
        initialPosition: pos,
      };
      break;
  }

  mouseDown = true;
  // }

  yield put(
    updateSheetView({
      sheetId,
      temporarySelection,
      selecting: !!temporarySelection,
      selections,
      activeCell,
      activeCellArea,
    })
  );
}
// #endregion

// #region cell mouse hover
export function* cellMouseHoverMi({ payload }: ReturnType<typeof cellHover>) {
  const { sheetId } = payload;
  const activeSheetId: string = yield select(
    (state: RootState) => state.sheetsManager.activeSheetId
  );
  const { sheet, sheetView }: SheetAndView = yield select((state) =>
    selectSheetAndView(state, sheetId)
  );

  if (sheetId !== activeSheetId) return;
  if (sheetView.temporarySelection && !payload.fromMouseMove) return;

  const updates = handleMouseHoverSelection(sheet, sheetView, payload);
  // console.log('updates', updates);

  yield put(
    updateSheetView({
      sheetId,
      ...updates,
    })
  );
}
// #endregion

// #region stop cell selection
export function* stopCellSelectionMi({
  payload: sheetId,
}: ReturnType<typeof stopCellSelection>) {
  const { sheet, sheetView }: SheetAndView = yield select((state) =>
    selectSheetAndView(state, sheetId)
  );
  const window: AppWindow | undefined = yield select(
    findWindowBySheetId(sheetId)
  );
  if (!window) return;

  let selections, activeCell, activeCellArea;

  const updates: Partial<SheetView> & {
    newArea: Area;
  } = yield updateSelectionOnMouseStop(
    sheet,
    sheetView,
    window.width,
    window.height
  );
  // console.log('updates', updates);
  if (updates?.newArea) selections = [createSheetSelection(updates.newArea)];
  if (updates?.selections) selections = updates.selections;
  if (updates?.activeCell) {
    activeCell = updates.activeCell;
    activeCellArea = unionAreaWithMerges(
      sheet.merges,
      convertPositionToArea(activeCell)
    );
  }
  // if (updates?.viewport) viewport = updates.viewport;

  mouseDown = false;

  yield put(
    updateSheetView({
      sheetId,
      selections,
      activeCell,
      activeCellArea,
      rowViewport: updates?.rowViewport,
      colViewport: updates?.colViewport,
      // viewport,
      selecting: false,
    })
  );
}
// #endregion

// #region mouse move with selection
let recursiveIteration = false;
export function* mouseMoveWithSelectionMi({
  payload,
}: ReturnType<typeof mouseMoveWithSelection>) {
  recursiveIteration = false;
  const window: AppWindow | undefined = yield select(
    findWindowBySheetId(payload.sheetId)
  );
  if (!window) return;

  const { sheetId, clientX, clientY, freezedColsSum, freezedRowsSum, ctrlKey } =
    payload;

  const sheet: Sheet = yield select((state: RootState) =>
    selectSheet(state, sheetId)
  );
  const sheetView: SheetView = yield select((state: RootState) =>
    selectSheetView(state, sheetId)
  );
  const isRtl = getI18n().dir() === 'rtl';

  // Get Boundaries
  const { paneBoundaries, freezeBoundaries, unFreezedCi, unFreezedRi } =
    getPanesBoundaries(
      window.x,
      window.y,
      window.width,
      window.height,
      sheetView.colViewport,
      sheetView.rowViewport,
      freezedColsSum,
      freezedRowsSum,
      sheet,
      isRtl,
      sheetView.filterResults
    );

  // Check if position is inside cell manages selection will be managed by hover
  if (
    inRange(clientX, paneBoundaries.minX, paneBoundaries.maxX) &&
    inRange(clientY, paneBoundaries.minY, paneBoundaries.maxY) &&
    !sheetView.temporarySelection
  ) {
    recursiveIteration = false;
    return;
  }
  recursiveIteration = true;

  yield call(
    recursiveScroll,
    sheetId,
    clientX,
    clientY,
    freezeBoundaries,
    paneBoundaries,
    window.width,
    window.height,
    isRtl,
    unFreezedCi,
    unFreezedRi,
    ctrlKey
  );
}
// #endregion

// #region recursive scroll
let lastScrolledSheetId = '';
let lastPosition = { ci: -1, ri: -1 };
function* recursiveScroll(
  sheetId: string,
  clientX: number,
  clientY: number,
  freezeBoundaries: Boundaries,
  paneBoundaries: Boundaries,
  windowWidth: number,
  windowHeight: number,
  isRtl: boolean,
  unFreezedCi?: number,
  unFreezedRi?: number,
  ctrlKey?: boolean
) {
  while (mouseDown && recursiveIteration) {
    const { sheet, sheetView }: SheetAndView = yield select((state) =>
      selectSheetAndView(state, sheetId)
    );

    const selection = _.last(sheetView.selections);

    let position: CellPosition = yield getCellPositionFromMousePosition(
      sheet,
      clientX,
      clientY,
      freezeBoundaries,
      paneBoundaries,
      sheetView.colViewport,
      sheetView.rowViewport,
      // sheetView.viewport,
      isRtl,
      unFreezedCi,
      unFreezedRi,
      sheetView.temporarySelection
        ? sheetView.temporarySelection.area
        : selection?.area,
      sheetView.filterResults
    );

    // Check to save unncessary iteration
    if (_.isEqual(position, lastPosition) && lastScrolledSheetId === sheetId)
      break;

    // console.log('ultimate position', position);

    yield call(cellMouseHoverMi, {
      type: cellHover.toString(),
      payload: {
        sheetId,
        cell: selection?.cell ?? SelectionCell.Cell,
        position,
        ctrlKey,
        focusCellInViewport: true,
        fromMouseMove: true,
        windowHeight,
        windowWidth,
      },
    });

    lastPosition = position;
    lastScrolledSheetId = sheetId;

    yield delay(30);
  }
}
// #endregion
